import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Check, Search, CircleMinus, CircleCheck, Edit2 } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning, confirmActive } from '../../common/confirm';
import NoDataFound from '../../common/NoDataFound';
import StatusSwitch, { StatusValue } from '../../common/StatusSwitch';
import { ProcedureTemplate, checklistTemplateService, ChecklistTemplateItem } from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';

interface ProcedureChecklistProps {
    procedure: ProcedureTemplate;
}

interface FormState {
    check_question: string;
    evidence_type: string;
    na_allowed: boolean;
}

const EVIDENCE_TYPES = ['None', 'Document', 'Screenshot', 'Excel', 'Text Explanation', 'External Link'];
const EVIDENCE_OPTIONS = EVIDENCE_TYPES.map(t => ({ value: t, label: t }));
const NA_OPTIONS = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];

const blankForm: FormState = { check_question: '', evidence_type: 'None', na_allowed: true };

const ProcedureChecklist: React.FC<ProcedureChecklistProps> = ({ procedure }) => {
    const [items, setItems] = useState<ChecklistTemplateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ChecklistTemplateItem | null>(null);
    // View-first edit: existing items open read-only; "Edit" enables the form.
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<FormState>(blankForm);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusValue>('ACTIVE');
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const list = await checklistTemplateService.listForProcedure(procedure.id);
            setItems(list);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load checklist items.') });
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => { setPage(1); }, [searchTerm, pageSize, statusFilter]);

    useEffect(() => {
        if (isModalOpen) {
            setSubmitting(false);
            setToggling(false);
        }
    }, [isModalOpen, currentItem]);

    const filtered = items.filter(i => {
        const matchesSearch = i.check_question.toLowerCase().includes(searchTerm.toLowerCase());
        const active = (i.is_active ?? 1) === 1;
        const matchesStatus = statusFilter === 'ACTIVE' ? active : !active;
        return matchesSearch && matchesStatus;
    });
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const handleAdd = useCallback(() => {
        setCurrentItem(null);
        setFormData(blankForm);
        setIsEditing(true); // create is always editable
        setModalOpen(true);
    }, []);

    const handleEdit = (item: ChecklistTemplateItem) => {
        setCurrentItem(item);
        const evType = item.evidence_type ? item.evidence_type : (item.evidence_required ? 'Document' : 'None');
        setFormData({ check_question: item.check_question, evidence_type: evType, na_allowed: item.na_allowed !== false });
        setIsEditing(false); // open read-only; "Edit" enables the form
        setModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentItem) return;
        const isActive = (currentItem.is_active ?? 1) === 1;
        const next = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this checklist item?' : 'Inactivate this checklist item?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await checklistTemplateService.update(currentItem.id, { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (fieldsDisabled) return;
        if (!formData.check_question.trim()) return;
        const required = formData.evidence_type !== 'None';
        const payload = {
            check_question: formData.check_question,
            evidence_required: required,
            evidence_type: required ? formData.evidence_type : null,
            na_allowed: formData.na_allowed,
        };
        setSubmitting(true);
        try {
            if (currentItem) {
                await checklistTemplateService.update(currentItem.id, payload);
                setToast({ type: 'success', message: 'Checklist item updated.' });
            } else {
                await checklistTemplateService.create({ procedure_template_id: procedure.id, is_active: 1, ...payload });
                setToast({ type: 'success', message: 'Checklist item created.' });
            }
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = !!currentItem;
    const isItemActive = (currentItem?.is_active ?? 1) === 1;
    // View-first: existing items are read-only until "Edit" is clicked.
    const fieldsDisabled = isEditMode && !isEditing;

    return (
        <>
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter}
                    onChange={setStatusFilter}
                    noun="checklist items"
                    testId="SEL_checklist_template_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_checklist_template_search"
                    />
                    <Button variant="fill" icon={Plus} data-testid="BTN_checklist_template_create" onClick={handleAdd}>Create</Button>
                </div>
            </div>

            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Checklist Item</th>
                                <th className="table-list-th">Evidence</th>
                                <th className="table-list-th">N/A Allowed</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', '2xs']} />}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={4} className="table-list-cell"><NoDataFound title="No Checklist Items" message="No checklist items defined yet." /></td></tr>
                            )}
                            {!loading && paginatedRows.map((item, idx) => (
                                <tr key={item.id} className="table-list-row" data-testid={`BTN_checklist_template_row_${item.id}_open`} onClick={() => handleEdit(item)}>
                                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                    <td className="table-list-cell">
                                        <span className="table-list-cell-title">{item.check_question}</span>
                                    </td>
                                    <td className="table-list-cell">
                                        {item.evidence_type && item.evidence_type !== 'None' ? (
                                            <span className="status-badge xs info">{item.evidence_type}</span>
                                        ) : item.evidence_required ? (
                                            <span className="status-badge xs danger">Required</span>
                                        ) : (
                                            <span className="text-gray-400 text-xs italic">No evidence</span>
                                        )}
                                    </td>
                                    <td className="table-list-cell">{item.na_allowed === false ? 'No' : 'Yes'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    recordLabel="items"
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} size="md">
                <Modal.Header>
                    {isEditMode
                        ? <>{isEditing ? 'Editing' : 'Viewing'} "<span className="item-highlight">{currentItem!.check_question}</span>"</>
                        : 'Create Checklist Item'}
                </Modal.Header>
                <Modal.Body>
                    <form id="checklist-item-form" data-testid="MOD_checklist_template_form" onSubmit={handleSave} className="flex flex-col gap-y-4">
                        <FormControl.Textarea
                            label="Item Description"
                            required
                            data-testid="INP_checklist_template_question"
                            value={formData.check_question}
                            onChange={e => setFormData({ ...formData, check_question: e.target.value })}
                            autoFocus
                            disabled={fieldsDisabled}
                            placeholder="e.g. Verify that the server room is locked..."
                        />
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <FormControl.SearchSelect
                                label="Evidence Type"
                                data-testid="SEL_checklist_template_evidence_type"
                                options={EVIDENCE_OPTIONS}
                                value={formData.evidence_type}
                                onChange={v => setFormData({ ...formData, evidence_type: String(v ?? 'None') })}
                                disabled={fieldsDisabled}
                            />
                            <FormControl.SearchSelect
                                label="N/A Allowed?"
                                data-testid="SEL_checklist_template_na_allowed"
                                options={NA_OPTIONS}
                                value={formData.na_allowed ? 'Yes' : 'No'}
                                onChange={v => setFormData({ ...formData, na_allowed: v === 'Yes' })}
                                disabled={fieldsDisabled}
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
                    {isEditMode && (
                        <Button
                            variant={isItemActive ? 'inactive-outline' : 'active-outline'}
                            icon={isItemActive ? CircleMinus : CircleCheck}
                            data-testid="BTN_checklist_template_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : isItemActive ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    {/* Create → Submit. */}
                    {!isEditMode && (
                        <Button
                            type="submit"
                            form="checklist-item-form"
                            variant="fill"
                            icon={Save}
                            data-testid="BTN_checklist_template_submit"
                            disabled={submitting || toggling || !formData.check_question.trim()}
                        >
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    )}
                    {/* View mode → "Edit" enables the form. */}
                    {isEditMode && !isEditing && (
                        <Button
                            variant="fill"
                            icon={Edit2}
                            data-testid="BTN_checklist_template_edit"
                            onClick={() => setIsEditing(true)}
                            disabled={submitting || toggling}
                        >
                            Edit
                        </Button>
                    )}
                    {/* Edit mode → "Update" saves. */}
                    {isEditMode && isEditing && (
                        <Button
                            type="submit"
                            form="checklist-item-form"
                            variant="update"
                            icon={Check}
                            data-testid="BTN_checklist_template_submit"
                            disabled={submitting || toggling || !formData.check_question.trim()}
                        >
                            {submitting ? 'Updating...' : 'Update'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ProcedureChecklist;
